<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import AFButton from '@/components/AFButton.vue';
import InfoPopover from '@/components/popovers/InfoPopover.vue';
import { useDisplay } from 'vuetify';

const props = defineProps<{
  file: File | null;
  isUploading: boolean;
  error: 'type' | 'size' | null;
  setFile: (f: File | null) => void;
  clearFile: () => void;
}>();

const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const { smAndDown } = useDisplay();

const textContent = computed(() =>
  !smAndDown.value ? 'general.media.upload.body_web' : 'general.media.upload.body_mobile',
);

function onPickFile() {
  fileInput.value?.click();
}
function onFileChange(e: Event) {
  const files = (e.target as HTMLInputElement).files;
  props.setFile(files && files.length ? files[0] : null);
}

function onDragOver() {
  isDragging.value = true;
}

function onDragLeave() {
  isDragging.value = false;
}

function onDrop(e: DragEvent) {
  isDragging.value = false;
  const f = e.dataTransfer?.files?.[0] ?? null;
  props.setFile(f);
}

function onClear() {
  props.clearFile();
  if (fileInput.value) fileInput.value.value = '';
}

watch(
  () => props.file,
  (file) => {
    if (!file && fileInput.value) fileInput.value.value = '';
  },
);
</script>

<template>
  <div class="w-full">
    <input
      ref="fileInput"
      type="file"
      name="cv"
      accept=".pdf,.txt"
      class="hidden"
      aria-labelledby="cv-upload-label"
      @change="onFileChange"
    />
    <div
      class="w-full border border-dashed rounded-md px-6 py-10 cursor-pointer transition flex flex-col items-center gap-1"
      :class="isDragging ? 'bg-af-grey-200' : 'hover:bg-af-grey-200'"
      data-cy="cv-upload-file-dropzone"
      @click="onPickFile"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
      role="button"
    >
      <v-icon icon="fa fa-file-arrow-down" :size="21" />
      <p id="cv-upload-label" class="text-sm flex items-center text-center gap-1">
        {{
          $t(textContent, {
            image: 1,
          })
        }}
        <InfoPopover :iconSize="4" :tooltip="true">
          <template #title>
            <span>{{ $t('profile.cv.info.title') }}</span>
          </template>
          <template #content>
            <v-sheet width="18.75rem">
              {{ $t('profile.cv.existing_cv.body_2', { max_size: 2 }) }}
            </v-sheet>
          </template>
        </InfoPopover>
      </p>
    </div>

    <!-- file name -->
    <div v-if="file" class="mt-4 text-af-grey-700 flex items-center gap-2 px-4">
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <v-icon icon="fa fa-file-lines" :size="28" />
        <p class="text-xs truncate font-light">{{ file.name }}</p>
      </div>
      <AFButton
        type="button"
        class="p-3 w-7 h-7 min-w-7 flex items-center justify-center"
        data-cy="clear-upload-file-btn"
        size="auto"
        variant="secondary"
        @click="onClear"
      >
        <v-icon icon="fa-xmark" :size="14" />
        <v-tooltip activator="parent" location="left">
          {{ $t('general.remove') }}
        </v-tooltip>
      </AFButton>
    </div>
    <!-- error -->
    <p v-show="error" class="mt-2 text-xs text-red-500 text-left">
      <v-icon icon="fat fa-circle-exclamation" class="me-1" :size="14" />
      <span v-show="error === 'type'">
        {{ $t('error.user.cv.format', { horizontal_list: 'pdf, txt' }) }}
      </span>
      <span v-show="error === 'size'">
        {{ $t('error.user.photo.file_size', { size: 2 }) }}
      </span>
    </p>
  </div>
</template>
